import Close16 from '@carbon/icons-react/es/close/16';
import { Row } from 'antd';
import { duration } from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { DatePicker } from 'components/atoms/date-picker';
import { Drawer as DrawerAtom } from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import {
  ContentHeader as ContentHeaderComponent,
  FilterWrapper,
} from 'components/molecules/filter-drawer/styles';
import { DrawerContent } from 'components/organisms/task-pane/task-detail';
import { FilterableField } from 'components/organisms/tasks/fields';
import { FilterType } from 'components/organisms/tasks/filter-box/filter-type';
import { AdvancedGroupedFilterComponent } from 'components/organisms/tasks/filter-box/grouped-advanced';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { useFieldsAsGroupedFields } from 'components/pages/all-tasks/use-fields-as-grouped-fields';
import { OutlineError } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useVendorRolesQuery } from 'queries/roles';
import { getRandomColor } from 'utils/get-random-color';
import { AvailabilityFilter } from '../types';
import { removeInsignificantTrailingZero } from '../utils/dates';
import { getRolesFromFilter } from './utils';

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-05);
`;
const drawerWidth = 880;
const Drawer = styled(DrawerAtom).attrs({
  width: drawerWidth,
  placement: 'right',
  closable: true,
  closeIcon: <Close16 />,
  mask: true,
  destroyOnClose: true,
})`
  .ant-drawer-body {
    padding: 0;
    ${DrawerContent} {
      width: ${drawerWidth}px;
    }
  }
`;

const DatePickerWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-05);
  ${Form.Item} {
    width: 100%;
    ${DatePicker} {
      width: 100%;
    }
  }
`;
const ContentHeader = styled(ContentHeaderComponent)`
  margin: var(--spacing-05) 0;
`;
const FilterHeading = styled.div`
  text-transform: uppercase;
  color: var(--gray-50);
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-05);
  &::after {
    content: '';
    border-top: 1px solid var(--border-color);
    width: 100%;
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item-has-error .ant-picker {
    ${OutlineError};
    &:focus {
      box-shadow: none;
      ${OutlineError};
    }
  }
`;

interface Props {
  availabilityFilter?: AvailabilityFilter;
  appliedFilter: GroupedFilter;
  userFields: FilterableField[];
  onFind: (params: {
    availabilityFilter?: AvailabilityFilter;
    filter: GroupedFilter;
  }) => void;
  visible: boolean;
  onCancel: () => void;
  isHoursPerWeek: boolean;
}

export const AvailabilityFilterDrawer: FC<Props> = ({
  availabilityFilter,
  appliedFilter,
  userFields,
  onFind,
  visible,
  onCancel,
  isHoursPerWeek,
}) => {
  const requiredMessage = useTranslation('input.required');
  const errorForHours = useTranslation(
    'resource_management.allocations.time_per_day.error',
  );

  const [filter, setFilter] = useState(appliedFilter);

  const [form] = Form.useForm();
  const { data: roles } = useVendorRolesQuery();
  const { groupedFields } = useFieldsAsGroupedFields({
    userFields,
  });

  const roleOptions = useMemo(
    () =>
      (roles ?? []).map((role) => ({
        label: role.roleName,
        value: role.roleId,
        color: getRandomColor(role.roleId),
      })),
    [roles],
  );
  const roleFilters = useMemo(
    () => (appliedFilter != null ? getRolesFromFilter(appliedFilter) : []),
    [appliedFilter],
  );

  const onSubmit = useCallback(() => {
    form
      .validateFields()
      .then(() => {
        const {
          startDate,
          endDate,
          hours,
          roles: choosenRoles = [],
        } = form.getFieldsValue();

        onFind({
          availabilityFilter: {
            startDate,
            endDate,
            availableMinutes: Number(hours) * 60,
            roles: (choosenRoles ?? []).map((role) => role.value),
          },
          filter,
        });
      })
      .catch(() => {});
  }, [filter, form, onFind]);

  useEffect(() => {
    if (visible) {
      if (availabilityFilter) {
        form.setFieldsValue({
          hours: availabilityFilter.availableMinutes / 60,
          startDate: availabilityFilter.startDate,
          endDate: availabilityFilter.endDate,
          roles: availabilityFilter.roles
            .map((id) => roleOptions.find((role) => role.value === id))
            .filter(Boolean),
        });
      } else {
        form.setFieldsValue({
          hours: duration(4, 'hours').asHours(),
          startDate: null,
          endDate: null,
          roles: roleFilters
            .map((id) => roleOptions.find((role) => role.value === id))
            .filter(Boolean),
        });
      }
      setFilter(appliedFilter);
    }
  }, [
    appliedFilter,
    availabilityFilter,
    form,
    roleFilters,
    roleOptions,
    visible,
  ]);

  return (
    <Drawer visible={visible} onClose={onCancel}>
      <DrawerContent>
        <DrawerContent.Title>
          <FormattedMessage id="resource_management.availability_criteria.trigger" />
        </DrawerContent.Title>
        <DrawerContent.Content>
          <StyledForm layout="vertical" form={form}>
            <DatePickerWrapper>
              <Form.Item
                label={<FormattedMessage id="start_date" />}
                required
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: requiredMessage,
                  },
                ]}
                shouldUpdate
              >
                <DatePicker
                  allowClear={false}
                  disabledDate={(date) => {
                    const endDate = form.getFieldValue('endDate');
                    const restrictMondays = date.day() !== 1 && isHoursPerWeek;

                    return (
                      restrictMondays ||
                      (endDate != null && date.isAfter(endDate, 'day'))
                    );
                  }}
                />
              </Form.Item>
              <Form.Item
                label={<FormattedMessage id="end_date" />}
                required
                name="endDate"
                rules={[
                  {
                    required: true,
                    message: requiredMessage,
                  },
                ]}
                shouldUpdate
              >
                <DatePicker
                  allowClear={false}
                  disabledDate={(date) => {
                    const startDate = form.getFieldValue('startDate');
                    const restrictFridays = date.day() !== 5 && isHoursPerWeek;

                    return (
                      restrictFridays ||
                      (startDate != null && date.isBefore(startDate, 'day'))
                    );
                  }}
                />
              </Form.Item>
            </DatePickerWrapper>
            <Form.Item
              label={
                <FormattedMessage
                  id={
                    isHoursPerWeek
                      ? 'resource_management.availability_criteria.available_minutes_per_week'
                      : 'resource_management.availability_criteria.available_minutes'
                  }
                />
              }
              name="hours"
              required
              rules={[
                () => ({
                  validator(_, value) {
                    const x = Number(removeInsignificantTrailingZero(value));

                    if (x <= 0 || x >= duration(24, 'hours').asHours()) {
                      return Promise.reject(new Error(errorForHours));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
              shouldUpdate
            >
              <InputNumber
                placeholder={useTranslation(
                  'resource_management.allocations.time_per_day.placeholder',
                )}
                formatter={(value) => `${value}h`}
                parser={(value) => value?.replace('h', '') ?? ''}
                onBlur={() => {
                  form.setFieldsValue({
                    hours: Number(
                      removeInsignificantTrailingZero(
                        form.getFieldValue('hours'),
                      ),
                    ),
                  });
                  form.validateFields(['hours']);
                }}
              />
            </Form.Item>
          </StyledForm>
          <FilterHeading>
            <FormattedMessage id="filters" />
          </FilterHeading>
          <ContentHeader>
            <Row>
              <FilterType
                value={filter.type}
                onChange={(type) => setFilter({ ...filter, type })}
              />
            </Row>
          </ContentHeader>
          <FilterWrapper>
            <AdvancedGroupedFilterComponent
              fields={groupedFields}
              filter={filter}
              onChange={setFilter}
            />
          </FilterWrapper>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <Footer>
            <Button onClick={onCancel}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button type="primary" onClick={onSubmit}>
              <FormattedMessage id="find" />
            </Button>
          </Footer>
        </DrawerContent.Footer>
      </DrawerContent>
    </Drawer>
  );
};
